import { InjectedConnector } from '@web3-react/injected-connector'
import { WalletConnectConnector } from '@web3-react/walletconnect-connector'
import { AbstractConnector } from '@web3-react/abstract-connector'
import { ChainId } from '@pancakeswap/sdk'
import { BscConnector } from '@binance-chain/bsc-connector'
import { ConnectorNames } from '@pancakeswap/uikit'
import { hexlify } from '@ethersproject/bytes'
import { toUtf8Bytes } from '@ethersproject/strings'
import { Web3Provider } from '@ethersproject/providers'
import {
  GRX_CHAIN_ID,
  ROBINHOOD_TESTNET_CHAIN_ID,
  ROBINHOOD_MAINNET_CHAIN_ID,
  SUPPORTED_CHAIN_IDS,
} from 'config/constants/chains'
import { getRpcUrlByChain } from './getRpcUrl'

const POLLING_INTERVAL = 12000
const chainId = GRX_CHAIN_ID
const rpcUrl = getRpcUrlByChain(GRX_CHAIN_ID)

export const injected = new InjectedConnector({ 
  supportedChainIds: SUPPORTED_CHAIN_IDS
})

const walletconnect = new WalletConnectConnector({
  rpc: {
    [GRX_CHAIN_ID]: getRpcUrlByChain(GRX_CHAIN_ID),
    [ROBINHOOD_TESTNET_CHAIN_ID]: getRpcUrlByChain(
      ROBINHOOD_TESTNET_CHAIN_ID,
    ),
    [ROBINHOOD_MAINNET_CHAIN_ID]: getRpcUrlByChain(
      ROBINHOOD_MAINNET_CHAIN_ID,
    ),
  },
  qrcode: true,
  pollingInterval: POLLING_INTERVAL,
})

const bscConnector = new BscConnector({ 
  supportedChainIds: SUPPORTED_CHAIN_IDS
})

export const connectorsByName = {
  [ConnectorNames.Injected]: injected,
  [ConnectorNames.WalletConnect]: walletconnect,
  [ConnectorNames.BSC]: bscConnector,
  [ConnectorNames.Blocto]: async () => {
    const { BloctoConnector } = await import('@blocto/blocto-connector')
    return new BloctoConnector({ chainId, rpc: rpcUrl })
  },
  [ConnectorNames.WalletLink]: async () => {
    const { WalletLinkConnector } = await import('@web3-react/walletlink-connector')
    const brandName = process.env.NEXT_PUBLIC_BRAND_NAME || 'GrxSwap'
    const logoUrl = process.env.NEXT_PUBLIC_BRAND_LOGO_URL || 'https://grxswap.io/logo.png'
    return new WalletLinkConnector({
      url: rpcUrl,
      appName: brandName,
      appLogoUrl: logoUrl,
      supportedChainIds: SUPPORTED_CHAIN_IDS,
    })
  },
} as const

export const getLibrary = (provider): Web3Provider => {
  const library = new Web3Provider(provider, 'any')
  library.pollingInterval = POLLING_INTERVAL
  return library
}

/**
 * BSC Wallet requires a different sign method
 * @see https://docs.binance.org/smart-chain/wallet/wallet_api.html#binancechainbnbsignaddress-string-message-string-promisepublickey-string-signature-string
 */
export const signMessage = async (
  connector: AbstractConnector,
  provider: any,
  account: string,
  message: string,
): Promise<string> => {
  if (window.BinanceChain && connector instanceof BscConnector) {
    const { signature } = await window.BinanceChain.bnbSign(account, message)
    return signature
  }

  /**
   * Wallet Connect does not sign the message correctly unless you use their method
   * @see https://github.com/WalletConnect/walletconnect-monorepo/issues/462
   */
  if (provider.provider?.wc) {
    const wcMessage = hexlify(toUtf8Bytes(message))
    const signature = await provider.provider?.wc.signPersonalMessage([wcMessage, account])
    return signature
  }

  return provider.getSigner(account).signMessage(message)
}
